import React, {useState, useRef, ReactNode, useCallback, useMemo} from 'react'
import SignInForm from './login/signInForm'
import CreateAccountForm from './create-account/createAccountForm'
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTrigger,
    DrawerTitle,
    DrawerDescription
} from '@/components/ui/drawer'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import ForgotPasswordModal from './login/forgotPasswordModal'
import {cn} from '@/lib/utils'
import {IBuilderPageReference} from 'hec/types/common'
import {useGetAccountFormData} from 'hec/builder/hooks/useGetAccountFormData'
import {isEditing} from '@builder.io/sdk-react'
import {invariant} from '../../helpers/invariant'

interface AccountDrawerProps {
    children?: ReactNode
}

export const AccountDrawer: React.FC<AccountDrawerProps> = ({children}) => {
    const isEdit = isEditing()
    const {formLabels, perksEnrollmentTermsContent, loginTitleContent, registrationTitleContent} =
        useGetAccountFormData()
    const [open, setOpen] = React.useState(false)
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'login' | 'registration'>('login')
    type PropagatedData = {
        checkoutPageRef?: IBuilderPageReference
        enableGuestCheckout?: boolean
    }

    const [propagatedData, setPropagatedData] = useState<PropagatedData | undefined>(undefined)

    const accountDrawerRef = useRef(null)

    const loginFormLabels = formLabels?.login
    const registrationFormLabels = formLabels?.registration
    invariant(registrationFormLabels, 'Registration form labels are required')

    const pageReference = propagatedData?.checkoutPageRef
    const guestCheckout = propagatedData?.enableGuestCheckout

    const handleOpenChange = (open: boolean) => {
        setOpen(open)
        if (open) return
        const closeDrawerEvent = new CustomEvent('closeDrawer')
        document.dispatchEvent(closeDrawerEvent)
    }

    const submitFormCallback = useCallback(() => {
        setOpen(false)
        document.body.style.position = ''
        document.body.style.top = ''
    }, [])

    const handleForgotPassword = useCallback(() => {
        setOpen(false)
        setForgotPasswordOpen(true)
    }, [])
    // the following useEffect is used to handle the drawer open and close events
    // it listens for custom events 'openDrawer' and 'closeDrawer'
    // when 'openDrawer' is triggered, it sets the drawer to open and updates the
    // propagated data with the detail from the event. This is usualy a page reference updated via builder
    React.useEffect(() => {
        const doc = document
        // Use stable handlers to avoid unnecessary rerenders
        const handleOpenDrawer = (e: Event) => {
            const customEvent = e as CustomEvent
            const checkoutPageRef = customEvent.detail.checkoutPageRef
            const enableGuestCheckout = customEvent.detail.enableGuestCheckout
            const tab = customEvent.detail.tab

            // Update active tab if provided
            if (tab === 'login' || tab === 'registration') {
                setActiveTab(tab)
            }

            setPropagatedData((previousStateValue) => {
                // Only update if either value changes
                if (
                    !previousStateValue ||
                    previousStateValue.checkoutPageRef !== checkoutPageRef ||
                    previousStateValue.enableGuestCheckout !== enableGuestCheckout
                ) {
                    return {checkoutPageRef, enableGuestCheckout}
                }
                return previousStateValue
            })
            setOpen((previousStateValue) => (previousStateValue ? previousStateValue : true))
        }
        const handleCloseDrawer = () => setOpen((prev) => (prev ? false : prev))
        doc.addEventListener('openDrawer', handleOpenDrawer)
        doc.addEventListener('closeDrawer', handleCloseDrawer)
        return () => {
            doc.removeEventListener('openDrawer', handleOpenDrawer)
            doc.removeEventListener('closeDrawer', handleCloseDrawer)
        }
    }, [])

    // Handle hash navigation for account drawer
    React.useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash
            if (hash === '#login' || hash === '#register') {
                const tab = hash === '#login' ? 'login' : 'registration'
                setActiveTab(tab)
                setOpen(true)
                // Clear hash after opening to prevent re-opening
                window.history.replaceState(
                    null,
                    '',
                    window.location.pathname + window.location.search
                )
            }
        }

        // Check initial hash on mount
        if (typeof window !== 'undefined') {
            handleHashChange()
        }

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange)
        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [])

    return (
        <>
            <Drawer
                open={open}
                onOpenChange={handleOpenChange}
                direction="left"
                data-testid="accountDrawer"
            >
                {children && (
                    <DrawerTrigger data-testid="accountDrawer-DrawerTrigger">
                        {children}
                    </DrawerTrigger>
                )}
                <DrawerContent
                    className={cn('sm:max-w-screen-sm', isEdit && 'h-[100vh]')}
                    ref={accountDrawerRef}
                    data-testid="accountDrawer-DrawerContent"
                >
                    <VisuallyHidden.Root>
                        <DrawerTitle data-testid="accountDrawer-DrawerTitle">
                            Account drawer
                        </DrawerTitle>
                        <DrawerDescription data-testid="accountDrawer-DrawerDescription">
                            This is an account drawer where you can sign into your account using
                            your account username and password. Also you can create a new account by
                            filling out the form in the Create tab.
                        </DrawerDescription>
                    </VisuallyHidden.Root>
                    <DrawerHeader data-testid="accountDrawer-DrawerHeader" />
                    <Tabs
                        value={activeTab}
                        onValueChange={(value) => setActiveTab(value as 'login' | 'registration')}
                        className="w-full"
                        data-testid="accountDrawer-Tabs"
                    >
                        <div className="flex">
                            <TabsList className="mx-auto" data-testid="accountDrawer-TabsList">
                                <TabsTrigger value="login" data-testid="accountDrawer-TabsTrigger">
                                    Sign in
                                </TabsTrigger>
                                <TabsTrigger
                                    value="registration"
                                    data-testid="accountDrawer-TabsTrigger"
                                >
                                    Create
                                </TabsTrigger>
                            </TabsList>
                        </div>
                        <TabsContent value="login" data-testid="accountDrawer-TabsContent">
                            <div className="px-8 space-y-5">
                                {loginTitleContent}
                                {useMemo(
                                    () => (
                                        <SignInForm
                                            closeDrawer={submitFormCallback}
                                            onForgotPassword={handleForgotPassword}
                                            formLabels={loginFormLabels}
                                            enableGuestCheckout={guestCheckout}
                                            checkoutPageReference={pageReference}
                                            enableLoginAndCheckout={true}
                                            data-testid="accountDrawer-SignInForm"
                                        />
                                    ),
                                    [
                                        submitFormCallback,
                                        handleForgotPassword,
                                        loginFormLabels,
                                        guestCheckout,
                                        pageReference
                                    ]
                                )}
                            </div>
                        </TabsContent>
                        <TabsContent value="registration" data-testid="accountDrawer-TabsContent">
                            <div className="px-8 space-y-5">
                                {registrationTitleContent}
                                <CreateAccountForm
                                    closeDrawer={submitFormCallback}
                                    formLabels={registrationFormLabels}
                                    perksEnrollmentTermsContent={perksEnrollmentTermsContent}
                                    data-testid="accountDrawer-CreateAccountForm"
                                />
                            </div>
                        </TabsContent>
                    </Tabs>
                </DrawerContent>
            </Drawer>
            <ForgotPasswordModal
                open={forgotPasswordOpen}
                setOpen={setForgotPasswordOpen}
                data-testid="accountDrawer-ForgotPasswordModal"
            />
        </>
    )
}
